import {
  type CountryName,
  countryNameToCode,
} from './country-database.js';
import type { Country } from './shared.js';
import { ConversionProcess } from './shared.js';

export const feedstockOther = {
  name: 'other',
  label: 'Other',
  /** Value irrelevant, set manually */
  isInEuRed2Annex9PartA: false,
  /** Value irrelevant, set manually */
  isSolidWaste: false,
  /** Value irrelevant, set manually */
  wasCertifiedAsProductionResidue: false,
  /** Value irrelevant, set manually */
  isEnergyCrop: false,
  /** Value irrelevant, set manually */
  isLanduseBasedCropBased: false,
  corsiaDefaultIlucLcaLteZeroCb: () => false,
} as const;

type FeedstockDbEntry = {
  name: string;
  isSolidWaste: boolean;
  wasCertifiedAsProductionResidue: boolean;
  isEnergyCrop: boolean;
  isLanduseBasedCropBased: boolean;
  isInEuRed2Annex9PartA: boolean;
  /**
   * corsiaDefaultIlucLcaLteZero must be set to value returned by this
   */
  corsiaDefaultIlucLcaLteZeroCb: (params: {
    conversionProcess: ConversionProcess;
    feedstockCountries: Country[];
  }) => boolean;
  label: string;
};

// raw feedstock database as const to have access to typed named
const rawFeedstockDatabase = [
  {
    name: 'agriculturalResidues',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Agricultural residues',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'algae',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Algae',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'animalFatsOilsAndOther',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Animal fats, oils and other animal processing by-products',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'atmosphericCO2FromDirectAirCapture',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Atmospheric CO2 from direct air capture',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'biogenicCO2AndPrecursors',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Biogenic CO2 and precursors (from fermentation and gasification)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'biowaste',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Biowaste',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'blackBrownLiquorAndPulpMill',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Black-liquor, brown liquor and pulp mill',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'brassicaCarinataOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Brassica carinata oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: ({ conversionProcess }) => conversionProcess === ConversionProcess.HEFA,
  },
  {
    name: 'camelinaOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Camelina oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: ({ conversionProcess }) => conversionProcess === ConversionProcess.HEFA,
  },
  {
    name: 'cerealsExcludingMaize',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Cereals excluding maize',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'cornMaizeOrGrain',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Corn (maize) or corn grain',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'cornOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Corn oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'cornOilTechnical',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Corn oil (technical)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'fatsOilsAndGrease',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Fats, oils and grease (from wastewater)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'fattyAcidsAndAlcohols',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Fatty acids and alcohols (from distillation and esters)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'foodWastes',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Food wastes',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'fossilCO2CapturedFromIndustrialProcesses',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Fossil CO2 captured from industrial processes',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'geologicalSourcesOfCO2ReleasedNaturally',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Geological sources of CO2 previously released naturally',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'glycerine',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Crude glycerine',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'grapeMarcsAndWineLees',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Grape marcs and wine lees',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'harvestingResidues',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Harvesting residues  (straw, husks, empty palm fruit bunches, cleaned cobs, tops, stem, limbs, saplings and leaves)',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'hydrogen',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Hydrogen',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'industrialWaste',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Industrial waste',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'jatrophaOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Jatropha oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: ({
      conversionProcess,
      feedstockCountries,
    }) => conversionProcess === ConversionProcess.HEFA && checkFeedstockCountry(feedstockCountries, ['India']),
  },
  {
    name: 'manureOrSewageSludge',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Animal manure and/or sewage sludge',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'miscanthus',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: true,
    isLanduseBasedCropBased: true,
    label: 'Miscanthus (herbaceous energy crops)',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: ({ conversionProcess }) => {
      return [
        ConversionProcess.FT,
        ConversionProcess.ATJIsobutanol,
        ConversionProcess.ATJStandalone,
        ConversionProcess.ATJIntegrated,
      ].includes(conversionProcess);
    },
  },
  {
    name: 'molasses',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Molasses',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'municipalSolidWaste',
    wasCertifiedAsProductionResidue: true,
    isSolidWaste: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Municipal solid waste (MSW)',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'nonBiogenicCO2AndPrecursors',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Non-biogenic CO2 and precursors (in waste gas)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'offSpecMaterialFromProductionProcesses',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Off-spec material from production processes that would otherwise be landfilled or incinerated',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'palmFattyAcidDistillate',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Palm fatty acid distillate (PFAD)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'palmKernelMeal',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Palm kernel meal',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'palmOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Palm oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'palmOilMillEffluent',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Palm oil mill effluent',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'poplar',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: true,
    isLanduseBasedCropBased: true,
    label: 'Poplar (short-rotation woody crops)',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: ({ conversionProcess, feedstockCountries }) => {
      return conversionProcess === ConversionProcess.FT && checkFeedstockCountry(feedstockCountries, ['United States of America']);
    },
  },
  {
    name: 'processingResidues',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Processing residues (bagasse, nutshells, bran, gums, soap stocks, pomace and palm kernel shells)',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'processingResiduesFromChemical', // <- should be productionResidues but whatever
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Production residues from chemical production processes',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'rapeSeed',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Rape seed',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'rapeSeedOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Rape seed oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'recyclableMaterialsOfNonBiologicalOrigin',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Recyclable materials of non-biological origin (mixed plastic waste, waste textiles, end-of-life tires)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'recycledCarbonFuels',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Recycled carbon fuels',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'renewableSources',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Renewable sources of ETBE, MTBE, TAEE, TAME, THxEE, and/or THxME',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'sawdustShavingsBark',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Sawdust, shavings, bark',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'soybean',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Soybean',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'soybeanOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Soybean oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'spoiledOrContaminatedGrain',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Spoiled or contaminated grain or other commodities',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'straw',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Straw',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'sugarbeet',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Sugarbeet',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'sugarcane',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Sugarcane',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'sunflower',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Sunflower',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'sunflowerOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Sunflower oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'switchgrass',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: true,
    isLanduseBasedCropBased: true,
    label: 'Switchgrass (herbaceous energy crops)',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: ({ conversionProcess, feedstockCountries }) => {
      const cond1 = [
        ConversionProcess.FT,
        ConversionProcess.ATJIsobutanol,
        ConversionProcess.ATJStandalone,
        ConversionProcess.ATJIntegrated,
      ].includes(conversionProcess) && checkFeedstockCountry(feedstockCountries, ['United States of America']);

      const cond2 = [
        ConversionProcess.ATJStandalone,
        ConversionProcess.ATJIntegrated,
      ].includes(conversionProcess);

      return cond1 || cond2;
    },
  },
  {
    name: 'synthesisGas',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Synthesis gas',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'syntheticLiquidFuels',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Synthetic liquid fuels',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'tallOilAndTallOilPitch',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Tall oil and tall oil pitch',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'tallow',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Tallow',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'usedCookingOilOrWasteCookingOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Used cooking oil and/or waste cooking oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'usedSolvents',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Used solvents',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'vegetableAcidOils',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Vegetable acid oils (from refining, neutralisation and other residues)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'wasteAndResiduesFromAgricultureForestryFisheriesAndAquaculture',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Waste and residues from agriculture, forestry, fisheries and aquaculture',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'wasteGases',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Waste gases',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'wastePlastics',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Waste plastics (polyethylene, polypropylene)',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'wasteVegetableOrAnimalOil',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Waste vegetable or animal oil',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'wastewater',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: true,
    isEnergyCrop: false,
    isLanduseBasedCropBased: false,
    label: 'Wastewater',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => true,
  },
  {
    name: 'wheat',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Wheat',
    isInEuRed2Annex9PartA: false,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'wheatStraw',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Wheat straw',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  {
    name: 'wood',
    isSolidWaste: false,
    wasCertifiedAsProductionResidue: false,
    isEnergyCrop: false,
    isLanduseBasedCropBased: true,
    label: 'Wood chips',
    isInEuRed2Annex9PartA: true,
    corsiaDefaultIlucLcaLteZeroCb: () => false,
  },
  feedstockOther,
] as const satisfies FeedstockDbEntry[];

// generalized version for ease of use
export const feedstockDatabase: FeedstockDbEntry[] = rawFeedstockDatabase;

export type FeedstockName = typeof rawFeedstockDatabase[number]['name'];

export const feedstockDatabaseSelectList = feedstockDatabase.map(entry => ({
  label: entry.label,
  value: entry.name,
}));

/** Feedstock country cannot have any country outside whitelist to meet corsia criteria */
const checkFeedstockCountry = (selectedCountries: Country[], whitelist: CountryName[]): boolean => {
  // we check codes because I believe they are more bulletproof than names, but use names for argument because its more descriptive
  const whitelistCodes = whitelist.map(countryName => countryNameToCode[countryName]);
  return selectedCountries.every(country => whitelistCodes.includes(country.code));
};
